/**
 * @license
 * Copyright 2022 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
} from 'firebase/firestore'
import { useEffect, useRef, useState } from 'react'
import type { ObservableStatus, FirestoreDocOptions } from '../firebase/firebase-services'
import {
  DENIAL_STREAK_TO_REPORT,
  noteFirestoreServerRead,
  scheduleRefusedReopen,
  subscribeFirestoreSessionHeal,
} from '../firestore-denial-reporter'
import {
  createListenHandle,
  sameDocument,
  trackListenConfirmation,
} from './listen-confirmation'
import { CONFIRMABLE_LISTEN_OPTIONS } from './listen-options'
import useModifyDocCallback, {
  type UseModifyDocCallback,
} from './use-modify-doc-callback'

const RETRY_DELAY_MS = 400
const MAX_RETRIES = 5
// The refused cadence is shared policy — see `scheduleRefusedReopen` in
// `firestore-denial-reporter.ts` (AGL-1066, AGL-1440, AGL-2944, AGL-2945).

/**
 * Raw `onSnapshot` listener with its own retry/backoff instead of
 * reactfire's `useFirestoreDocData` — that hook caches the query's RxJS
 * Observable, and once it errors the Observable is *terminated forever*:
 * remounting the component never reopens the Firestore subscription, so a
 * single transient `permission-denied` (e.g. right after sign-in, before
 * Firestore's own credential provider has attached the user's ID token)
 * permanently breaks every caller of `useHost`/`useLayout`/`useScreen`/
 * `useScreenVersion`/`useLayoutVersion` for the rest of the session
 * (AGL-216/217/223). A fresh `onSnapshot` call each retry opens a genuinely
 * new listener, so it recovers once the token has propagated.
 */
export function useDocData<T>(
  ref: DocumentReference<T>,
  options?: FirestoreDocOptions<T>,
): ObservableStatus<T> {
  const idField = options?.idField
  const initialData = options?.initialData
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>(
    initialData !== undefined ? 'success' : 'loading',
  )
  const [data, setData] = useState<T>(initialData as T)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [hasEmitted, setHasEmitted] = useState(initialData !== undefined)
  const [hasPendingWrites, setHasPendingWrites] = useState(false)
  // Un-confirmed until a snapshot says otherwise (AGL-1066).
  const [fromCache, setFromCache] = useState(true)
  const [serverDenied, setServerDenied] = useState(false)
  const [listen] = useState(createListenHandle)
  const resolveFirstValueRef = useRef<(() => void) | undefined>(undefined)
  const firstValuePromiseRef = useRef<Promise<void> | undefined>(undefined)
  if (!firstValuePromiseRef.current) {
    firstValuePromiseRef.current = new Promise((resolve) => {
      resolveFirstValueRef.current = resolve
    })
  }

  useEffect(() => {
    setStatus('loading')
    setError(undefined)

    let cancelled = false
    let unsubscribe: (() => void) | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    /** Cancels a pending reopen on the refused cadence (AGL-2944). */
    let cancelRefusedReopen: (() => void) | null = null
    let attempt = 0
    /**
     * Refusals since the last SERVER snapshot (AGL-1066).
     *
     * Still distinct from `attempt` after the flip: `attempt` is a BUDGET
     * that the heal broadcast may refund, whereas this is EVIDENCE about the
     * server that only a server snapshot may clear. Counted here to publish
     * `serverDenied` and to decide the slow cadence — the `session-health`
     * reporting the other two listener hooks do is a separate decision, and
     * this hook's callers (the besigner document family) are exactly the ones
     * a false verdict would hurt most.
     */
    let deniedStreak = 0
    /** Epoch ms of the current streak's first refusal (AGL-1440). */
    let deniedStreakStartedAt = 0
    /**
     * The retry budget is spent and the server's last word was a refusal —
     * what keeps `status: 'error'` from being undone by the cached emission
     * that precedes every reopened refusal (AGL-1066). See the longer note
     * on the same variable in `use-firestore-collection`.
     */
    let terminal = false
    /** What a save waits on while this listener is unconfirmed (AGL-4120). */
    const confirmation = trackListenConfirmation(listen)
    /** The cached snapshot on screen, while one is (AGL-4120). */
    let shownFromCache: DocumentSnapshot<DocumentData> | null = null

    const subscribe = () => {
      unsubscribe = onSnapshot(
        ref,
        // Without this the cache→server confirmation is never delivered and
        // `fromCache` latches true for the life of the listener, refusing
        // every guarded save on the page (AGL-2486). See `listen-options.ts`.
        CONFIRMABLE_LISTEN_OPTIONS,
        (snapshot) => {
          if (cancelled) return
          // Only the SERVER answering refunds the retry budget, and only a
          // server snapshot ends a refusal (AGL-1066).
          if (!snapshot.metadata.fromCache) {
            attempt = 0
            terminal = false
            deniedStreak = 0
            setServerDenied(false)
            // Evidence only, deliberately not the reporting call: this hook
            // stays out of `session-health` (see `deniedStreak` above), but
            // its server answers are still proof the session can read, which
            // is what `refusedRetryDelayMs` splits the cadence on (AGL-1440).
            noteFirestoreServerRead()
          } else if (deniedStreak === 0) {
            // Cached, but nothing has been refused since the server last
            // answered — not the AGL-1066 fault, so the budget is refunded
            // exactly as it always was. This is what keeps offline inert:
            // `unavailable` never enters `deniedStreak`.
            attempt = 0
          }
          if (!terminal) {
            setStatus('success')
            setError(undefined)
          }
          // Always true after ANY emission, cached included: this is what
          // tells a consumer "I have data, the server refused" apart from "I
          // never had data" — the distinction the besigner editors and the
          // host setup Theme tab blank on (AGL-1066).
          setHasEmitted(true)
          // Kept, not dropped: a snapshot that still carries this client's
          // own queued write is not evidence of what the store holds
          // (AGL-1262). Callers that record a "last saved" state need to
          // know the difference.
          setHasPendingWrites(snapshot.metadata.hasPendingWrites)
          setFromCache(snapshot.metadata.fromCache)
          confirmation.report(
            snapshot.metadata.fromCache,
            !snapshot.metadata.fromCache &&
              shownFromCache !== null &&
              !sameDocument(shownFromCache, snapshot),
          )
          shownFromCache = snapshot.metadata.fromCache ? snapshot : null
          const value = snapshot.exists()
            ? ({
                ...(snapshot.data() as object),
                ...(idField ? { [idField]: snapshot.id } : {}),
              } as T)
            : (undefined as T)
          setData(value)
          resolveFirstValueRef.current?.()
        },
        (err) => {
          if (cancelled) return
          confirmation.release()
          unsubscribe?.()
          // Only a refusal — a lost network produces no error callback at
          // all, and anything that does surface one carries `unavailable`,
          // which must never read as a dead session.
          if ((err as { code?: string })?.code === 'permission-denied') {
            deniedStreak += 1
            if (deniedStreak === 1) deniedStreakStartedAt = Date.now()
            if (deniedStreak >= DENIAL_STREAK_TO_REPORT) setServerDenied(true)
          }
          if (attempt < MAX_RETRIES) {
            attempt += 1
            if (deniedStreak > MAX_RETRIES) {
              cancelRefusedReopen = scheduleRefusedReopen(
                subscribe,
                deniedStreakStartedAt,
              )
            } else {
              timer = setTimeout(subscribe, RETRY_DELAY_MS)
            }
          } else {
            terminal = true
            setStatus('error')
            setError(err)
            resolveFirstValueRef.current?.()
            // A refusal streak keeps a slow road back for a recovery nobody
            // announced — see the same branch in `use-firestore-collection`.
            // The cadence splits on session-vs-ref evidence (AGL-1440), grows
            // with the streak's age (AGL-2945) and waits while the tab is
            // hidden (AGL-2944).
            if (deniedStreak > MAX_RETRIES) {
              cancelRefusedReopen = scheduleRefusedReopen(
                subscribe,
                deniedStreakStartedAt,
              )
            }
          }
        },
      )
    }
    subscribe()

    /**
     * Reopen when the session heals — see the long note on the same
     * subscription in `use-firestore-collection` (AGL-1066).
     *
     * This hook is where it counts most: its deps are `[ref.firestore,
     * ref.path]`, neither of which moves when a `stale` re-auth signs the
     * same uid back in, and it is what every besigner editor reads through.
     * Nothing else was ever going to bring those pages back without a reload.
     */
    const unsubscribeHeal = subscribeFirestoreSessionHeal(() => {
      if (cancelled || deniedStreak === 0) return
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      cancelRefusedReopen?.()
      cancelRefusedReopen = null
      unsubscribe?.()
      attempt = 0
      subscribe()
    })

    return () => {
      cancelled = true
      confirmation.close()
      unsubscribeHeal()
      if (timer) clearTimeout(timer)
      cancelRefusedReopen?.()
      unsubscribe?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.firestore, ref.path])

  return {
    status,
    hasEmitted,
    isComplete: false,
    data,
    error,
    firstValuePromise: firstValuePromiseRef.current,
    hasPendingWrites,
    fromCache,
    serverDenied,
    listen,
  }
}
export type UseDocData<T> = typeof useDocData<T>

export function useDoc<T>(
  ref: DocumentReference<T>,
  options?: FirestoreDocOptions<T>,
): {doc: ReturnType<UseDocData<T>>, setDoc: ReturnType<UseModifyDocCallback<T>>} {
  return {doc: useDocData(ref, options), setDoc: useModifyDocCallback(ref)}
}


export default useDoc
